import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { UserCircle, Mail, Shield, Calendar, Award, Edit, Camera, Save, X, Loader2, Fingerprint, Trash2, Moon, Sun, Layout, Sparkles, PanelLeftClose } from 'lucide-react';
import { toast } from 'sonner';
import { normalizeAppError } from '../../utils/apiErrors';
import { isPlatformAuthenticatorAvailable, registerBiometric } from '../../utils/webauthn';
import { resolveImageUrl } from '../../utils/resolveImageUrl';
import { API } from '../../config/apiBase';

export default function AccountPage() {
    const { user, checkAuth } = useAuth();
    const { isDark, toggleTheme, layoutMode, toggleLayoutMode } = useTheme();
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [editData, setEditData] = useState({ name: '', new_password: '' });
    const [uploading, setUploading] = useState(false);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [pendingFile, setPendingFile] = useState(null);
    const [tick, setTick] = useState(0);
    const fileInputRef = React.useRef(null);
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');

    const handleFileSelect = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) {
            toast.error('Formato invalido. Use PNG, JPG ou WebP.');
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            toast.error('Arquivo muito grande (max 5MB)');
            return;
        }
        setPendingFile(file);
        setPreviewUrl(URL.createObjectURL(file));
    };

    const confirmUpload = async () => {
        if (!pendingFile) return;
        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', pendingFile);
            const res = await fetch(`${API}/auth/upload-picture`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData,
            });
            if (res.ok) {
                toast.success('Foto atualizada!');
                setPendingFile(null);
                setPreviewUrl(null);
                if (checkAuth) checkAuth();
            } else {
                toast.error('Erro ao enviar foto');
            }
        } catch {
            toast.error('Erro de conexão');
        } finally {
            setUploading(false);
        }
    };

    const cancelPreview = () => {
        setPendingFile(null);
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
    };

    const startEdit = () => {
        setEditData({ name: user?.name || '', new_password: '' });
        setEditing(true);
    };

    const handleSave = async () => {
        if (!editData.name.trim()) { toast.error('Nome obrigatorio'); return; }
        setSaving(true);
        try {
            const body = { name: editData.name.trim() };
            if (editData.new_password) body.new_password = editData.new_password;
            const res = await fetch(`${API}/account`, {
                method: 'PUT',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify(body)
            });
            if (res.ok) {
                toast.success('Perfil atualizado!');
                setEditing(false);
                if (checkAuth) checkAuth();
            } else toast.error('Erro ao salvar');
        } catch { toast.error('Erro de conexão'); }
        finally { setSaving(false); }
    };

    return (
        <div className="sv-page" data-testid="account-page">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <h2 className="text-lg font-bold" style={{ color: 'var(--text)', fontFamily: "var(--font-display)" }}>Minha Conta</h2>
                {editing ? (
                    <div className="flex gap-2">
                        <button className="sv-btn-gold-system sv-btn-system" onClick={handleSave} disabled={saving} data-testid="account-save-btn">
                            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Salvar
                        </button>
                        <button className="sv-btn-system" onClick={() => setEditing(false)} data-testid="account-cancel-btn"><X size={14} /> Cancelar</button>
                    </div>
                ) : (
                    <button className="sv-btn-system" onClick={startEdit} data-testid="account-edit-btn"><Edit size={14} /> Editar Perfil</button>
                )}
            </div>

            <div className="sv-bento-grid">
                {/* Profile Card */}
                <div className="sv-card-system">
                    <div className="flex items-center gap-4 mb-6">
                        <div className="relative cursor-pointer" onClick={() => fileInputRef.current?.click()} data-testid="avatar-click-area">
                            <div className="w-20 h-20 rounded-full overflow-hidden transition-all hover:opacity-80" style={{ border: '1px solid var(--border)' }}>
                                {previewUrl ? (
                                    <img src={previewUrl} alt="Preview" className="w-full h-full object-cover" />
                                ) : user?.picture ? (
                                    <img src={resolveImageUrl(user.picture)} alt="" className="w-full h-full object-cover" />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center text-2xl font-bold" style={{ background: 'var(--surface-2)', color: 'var(--sv-gold-primary)' }}>
                                        {user?.name?.charAt(0).toUpperCase()}
                                    </div>
                                )}
                            </div>
                            <button
                                className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full flex items-center justify-center"
                                style={{ background: 'var(--sv-gold-btn)', color: 'var(--sv-on-action)' }}
                                onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
                                disabled={uploading}
                                data-testid="avatar-upload-btn"
                            >
                                {uploading ? <Loader2 size={12} className="animate-spin" /> : <Camera size={12} />}
                            </button>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/png,image/jpeg,image/webp"
                                onChange={handleFileSelect}
                                className="hidden"
                                data-testid="avatar-file-input"
                            />
                        </div>
                        <div>
                            {editing ? (
                                <input type="text" value={editData.name} onChange={e => setEditData(p => ({ ...p, name: e.target.value }))}
                                    className="text-lg font-bold px-3 py-1 rounded-lg"
                                    style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)', outline: 'none' }}
                                    data-testid="account-name-input" />
                            ) : (
                                <h3 className="text-lg font-bold" style={{ color: 'var(--text)' }}>{user?.name || 'Usuario'}</h3>
                            )}
                            <span className="sv-badge sv-badge-gold">{user?.role === 'admin' ? 'Administrador' : 'Usuario'}</span>
                            {previewUrl && (
                                <div className="flex gap-2 mt-2">
                                    <button className="flex items-center gap-1 px-3 py-1 rounded-lg text-[11px] font-semibold" style={{ background: 'var(--sv-gold-btn)', color: 'var(--sv-on-action)' }} onClick={confirmUpload} disabled={uploading} data-testid="avatar-confirm-btn">
                                        {uploading ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />} Salvar foto
                                    </button>
                                    <button className="flex items-center gap-1 px-2 py-1 rounded-lg text-[11px]" style={{ background: 'var(--surface-2)', color: 'var(--muted)', border: '1px solid var(--border)' }} onClick={cancelPreview} data-testid="avatar-cancel-btn">
                                        <X size={11} /> Cancelar
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="flex flex-col gap-3">
                        <InfoRow icon={Mail} label="Email" value={user?.email || '-'} />
                        <InfoRow icon={Shield} label="Role" value={user?.role || 'user'} />
                        <InfoRow icon={Award} label="Nivel" value={`${user?.level || 1} - ${user?.level_name || 'Novato'}`} />
                        <InfoRow icon={Calendar} label="Membro desde" value={user?.created_at ? new Date(user.created_at).toLocaleDateString('pt-BR') : '-'} />
                        {editing && (
                            <div className="pt-2" style={{ borderTop: '1px solid var(--border)' }}>
                                <label className="text-xs font-medium" style={{ color: 'var(--muted)' }}>Nova senha (opcional)</label>
                                <input type="password" value={editData.new_password} onChange={e => setEditData(p => ({ ...p, new_password: e.target.value }))}
                                    placeholder="Deixe vazio para manter"
                                    className="w-full mt-1 px-3 py-2 rounded-lg text-sm"
                                    style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)', outline: 'none' }}
                                    data-testid="account-password-input" />
                            </div>
                        )}
                    </div>
                </div>

                {/* Stats */}
                <div className="sv-card-system">
                    <div className="sv-card-header">
                        <span className="sv-card-label">Estatisticas</span>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <StatBox label="Pontos" value={user?.points || 0} />
                        <StatBox label="Ofertas" value={user?.deals_count || 0} />
                        <StatBox label="Comentarios" value={user?.comments_count || 0} />
                        <StatBox label="Likes Recebidos" value={user?.likes_received || 0} />
                    </div>
                    {/* Progress */}
                    <div className="mt-4 pt-4" style={{ borderTop: '1px solid var(--border)' }}>
                        <div className="flex justify-between text-xs mb-2">
                            <span style={{ color: 'var(--muted)' }}>Próximo nivel</span>
                            <span style={{ color: 'var(--text)' }}>{user?.points || 0} / {user?.next_level_threshold || 100}</span>
                        </div>
                        <div className="w-full h-2 rounded-full" style={{ background: 'var(--surface-2)' }}>
                            <div className="h-2 rounded-full" style={{ width: `${Math.min(((user?.points || 0) / (user?.next_level_threshold || 100)) * 100, 100)}%`, background: 'var(--sv-gold-primary)' }} />
                        </div>
                    </div>
                </div>

                {/* Biometria */}
                <BiometricSection token={token} />

                {/* Preferências de Interface */}
                <div className="sv-card-system" data-testid="interface-preferences">
                    <div className="sv-card-header">
                        <span className="sv-card-label flex items-center gap-2"><Layout size={14} /> Preferências de Interface</span>
                    </div>
                    <div className="flex flex-col">
                        <div className="flex items-center justify-between py-3" style={{ borderBottom: '1px solid var(--border)' }}>
                            <div>
                                <p className="text-sm font-medium" style={{ color: 'var(--text)' }}>Modo noturno</p>
                                <p className="text-xs mt-0.5" style={{ color: 'var(--muted-2)' }}>Alterna entre temas claro e escuro</p>
                            </div>
                            <div className="flex items-center gap-2">
                                <Sun size={14} style={{ color: !isDark ? 'var(--sv-gold-primary)' : 'var(--muted-2)' }} />
                                <button onClick={toggleTheme} className="w-10 h-6 rounded-full flex items-center transition-colors" style={{ background: isDark ? 'var(--sv-gold-primary)' : 'var(--surface-3)', border: '1px solid var(--border)', padding: '0 2px' }} data-testid="pref-theme-toggle">
                                    <div className="w-4 h-4 rounded-full transition-transform" style={{ background: isDark ? 'var(--bg)' : 'var(--muted-2)', transform: isDark ? 'translateX(16px)' : 'translateX(0)' }} />
                                </button>
                                <Moon size={14} style={{ color: isDark ? 'var(--sv-gold-primary)' : 'var(--muted-2)' }} />
                            </div>
                        </div>
                        <div className="flex items-center justify-between py-3" style={{ borderBottom: '1px solid var(--border)' }}>
                            <div>
                                <p className="text-sm font-medium" style={{ color: 'var(--text)' }}>Layout</p>
                                <p className="text-xs mt-0.5" style={{ color: 'var(--muted-2)' }}>Clássico ou Minimalista</p>
                            </div>
                            <div className="sv-tabs" style={{ padding: 2 }}>
                                <button className={`sv-tab ${layoutMode === 'classic' ? 'active' : ''}`} onClick={toggleLayoutMode} style={{ padding: '4px 10px', fontSize: 11 }} data-testid="pref-layout-classic">Clássico</button>
                                <button className={`sv-tab ${layoutMode === 'minimal' ? 'active' : ''}`} onClick={toggleLayoutMode} style={{ padding: '4px 10px', fontSize: 11 }} data-testid="pref-layout-minimal">Minimal</button>
                            </div>
                        </div>
                        <div className="flex items-center justify-between py-3" style={{ borderBottom: '1px solid var(--border)' }}>
                            <div>
                                <p className="text-sm font-medium" style={{ color: 'var(--text)' }}>Animações</p>
                                <p className="text-xs mt-0.5" style={{ color: 'var(--muted-2)' }}>Transições suaves na interface</p>
                            </div>
                            <button onClick={() => { const next = localStorage.getItem('sv_animations') === 'off' ? 'on' : 'off'; localStorage.setItem('sv_animations', next); document.documentElement.classList.toggle('sv-no-animations', next === 'off'); window.dispatchEvent(new Event('sv-settings-changed')); setTick(t => t + 1); }} className="w-10 h-6 rounded-full flex items-center transition-colors" style={{ background: localStorage.getItem('sv_animations') !== 'off' ? 'var(--sv-gold-btn)' : 'var(--surface-3)', border: '1px solid var(--border)', padding: '0 2px' }} data-testid="toggle-animations">
                                <div className="w-4 h-4 rounded-full transition-transform" style={{ background: localStorage.getItem('sv_animations') !== 'off' ? 'var(--bg)' : 'var(--muted-2)', transform: localStorage.getItem('sv_animations') !== 'off' ? 'translateX(16px)' : 'translateX(0)' }} />
                            </button>
                        </div>
                        <div className="flex items-center justify-between py-3">
                            <div>
                                <p className="text-sm font-medium" style={{ color: 'var(--text)' }}>Sidebar compacta</p>
                                <p className="text-xs mt-0.5" style={{ color: 'var(--muted-2)' }}>Reduzir largura da barra lateral</p>
                            </div>
                            <button onClick={() => { const next = localStorage.getItem('sv_compact_sidebar') === 'on' ? 'off' : 'on'; localStorage.setItem('sv_compact_sidebar', next); window.dispatchEvent(new Event('sv-settings-changed')); setTick(t => t + 1); }} className="w-10 h-6 rounded-full flex items-center transition-colors" style={{ background: localStorage.getItem('sv_compact_sidebar') === 'on' ? 'var(--sv-gold-btn)' : 'var(--surface-3)', border: '1px solid var(--border)', padding: '0 2px' }} data-testid="toggle-compact-sidebar">
                                <div className="w-4 h-4 rounded-full transition-transform" style={{ background: localStorage.getItem('sv_compact_sidebar') === 'on' ? 'var(--bg)' : 'var(--muted-2)', transform: localStorage.getItem('sv_compact_sidebar') === 'on' ? 'translateX(16px)' : 'translateX(0)' }} />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function InfoRow({ icon: Icon, label, value }) {
    return (
        <div className="flex items-center gap-3 py-2" style={{ borderBottom: '1px solid var(--border)' }}>
            <Icon size={14} style={{ color: 'var(--muted-2)' }} />
            <span className="text-xs font-medium" style={{ color: 'var(--muted)' }}>{label}</span>
            <span className="ml-auto text-sm font-medium" style={{ color: 'var(--text)' }}>{value}</span>
        </div>
    );
}

function StatBox({ label, value }) {
    return (
        <div className="p-3 rounded-xl" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)' }}>
            <p className="text-xs" style={{ color: 'var(--muted-2)' }}>{label}</p>
            <p className="text-xl font-bold mt-1" style={{ color: 'var(--text)', fontFamily: "var(--font-display)" }}>{value}</p>
        </div>
    );
}

function BiometricSection({ token }) {
    const [supported, setSupported] = useState(false);
    const [credentials, setCredentials] = useState([]);
    const [loading, setLoading] = useState(false);
    const [registering, setRegistering] = useState(false);

    useEffect(() => {
        isPlatformAuthenticatorAvailable().then(setSupported);
        fetchCreds();
    }, []);

    const fetchCreds = async () => {
        try {
            const t = localStorage.getItem('token') || sessionStorage.getItem('token');
            const res = await fetch(`${API}/auth/webauthn/credentials`, { headers: { 'Authorization': `Bearer ${t}` } });
            if (res.ok) { const d = await res.json(); setCredentials(d.credentials || []); }
        } catch {}
    };

    const handleRegister = async () => {
        setRegistering(true);
        try {
            const t = localStorage.getItem('token') || sessionStorage.getItem('token');
            await registerBiometric(t);
            toast.success('Biometria registrada!');
            fetchCreds();
        } catch (err) {
            toast.error(normalizeAppError(err).message);
        } finally {
            setRegistering(false);
        }
    };

    const handleDelete = async (credId) => {
        try {
            const t = localStorage.getItem('token') || sessionStorage.getItem('token');
            await fetch(`${API}/auth/webauthn/credentials/${credId}`, {
                method: 'DELETE', headers: { 'Authorization': `Bearer ${t}` }
            });
            toast.success('Credencial removida');
            fetchCreds();
        } catch { toast.error('Erro ao remover'); }
    };

    return (
        <div className="sv-card-system" data-testid="biometric-section">
            <div className="sv-card-header">
                <span className="sv-card-label flex items-center gap-2"><Fingerprint size={14} /> Biometria</span>
            </div>
            {!supported ? (
                <p className="text-xs" style={{ color: 'var(--muted-2)' }}>Biometria não suportada neste dispositivo. Acesse pelo celular para usar.</p>
            ) : (
                <>
                    {credentials.length > 0 ? (
                        <div className="flex flex-col gap-2 mb-3">
                            {credentials.map(c => (
                                <div key={c.credential_id} className="flex items-center gap-3 p-2.5 rounded-lg" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)' }}>
                                    <Fingerprint size={16} style={{ color: '#22c55e' }} />
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium" style={{ color: 'var(--text)' }}>{c.device_name || 'Dispositivo'}</p>
                                        <p className="text-[10px]" style={{ color: 'var(--muted-2)' }}>{new Date(c.created_at).toLocaleDateString('pt-BR')}</p>
                                    </div>
                                    <button onClick={() => handleDelete(c.credential_id)} className="p-1.5 rounded-lg hover:bg-red-500/10" data-testid={`delete-cred-${c.credential_id}`}>
                                        <Trash2 size={12} style={{ color: '#ef4444' }} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-xs mb-3" style={{ color: 'var(--muted-2)' }}>Nenhuma biometria cadastrada. Registre para login rápido.</p>
                    )}
                    <button
                        onClick={handleRegister}
                        disabled={registering}
                        className="sv-btn-gold-system sv-btn-system w-full justify-center"
                        data-testid="register-biometric-btn"
                    >
                        {registering ? <Loader2 size={14} className="animate-spin" /> : <Fingerprint size={14} />}
                        <span className="ml-1">{registering ? 'Registrando...' : 'Registrar biometria'}</span>
                    </button>
                </>
            )}
        </div>
    );
}
